import dayjs from "dayjs";
import { formatDuration, QuestionStat, QuizAttempt } from "./quizData";

const MARK = "#FF0072";
const MARK_SOFT = "#FFD6E8";
const GRID = "#F0F0F0";
const AXIS = "#8C8C8C";
const INK = "#1F2328";

const axisText = { fontSize: 11, fill: AXIS, fontFamily: "monospace" };
const labelText = { fontSize: 12, fill: "#5E5058" };
const valueText = { fontSize: 12, fill: INK, fontFamily: "monospace", fontWeight: 500 };

const shortName = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length <= 2) return name;
    // "Nombre SegundoNombre Apellido1 Apellido2" -> "Nombre Apellido1"
    return `${parts[0]} ${parts[parts.length >= 4 ? 2 : 1]}`;
};

// Barra con extremo redondeado del lado del dato y plana en la base.
const hbar = (x: number, y: number, w: number, h: number, r = 4) => {
    r = Math.min(r, w, h / 2);
    return `M${x},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h - r}Q${x + w},${y + h} ${x + w - r},${y + h}H${x}Z`;
};
const vbar = (x: number, y: number, w: number, h: number, r = 4) => {
    r = Math.min(r, h, w / 2);
    return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
};

const niceStep = (max: number, ticks: number) => {
    const raw = max / ticks;
    const steps = [30, 60, 120, 180, 300, 600, 900, 1800, 3600];
    return steps.find((s) => s >= raw) ?? Math.ceil(raw / 3600) * 3600;
};

export const DurationChart = ({ attempts, avg }: { attempts: QuizAttempt[]; avg: number }) => {
    const rows = [...attempts].sort((a, b) => a.durationSeconds - b.durationSeconds);
    const W = 460, L = 130, R = 48, T = 24, bh = 24, gap = 16;
    const H = T + rows.length * (bh + gap) + 22;
    const step = niceStep(Math.max(...rows.map((r) => r.durationSeconds), 60), 5);
    const max = Math.ceil(Math.max(...rows.map((r) => r.durationSeconds), 60) / step) * step;
    const x = (v: number) => L + (v / max) * (W - L - R);
    const ticks = Array.from({ length: max / step + 1 }, (_, i) => i * step);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Duración por participante">
            {ticks.map((v) => (
                <g key={v}>
                    <line x1={x(v)} x2={x(v)} y1={T - 4} y2={H - 20} stroke={v ? GRID : "#D9D9D9"} />
                    <text x={x(v)} y={H - 4} textAnchor="middle" style={axisText}>{formatDuration(v)}</text>
                </g>
            ))}
            {rows.map((a, i) => {
                const y = T + i * (bh + gap);
                return (
                    <g key={a.id}>
                        <title>{`${a.name} · ${formatDuration(a.durationSeconds)} min · ${(a.durationSeconds / (a.totalQuestions || 1)).toFixed(1)} s por pregunta`}</title>
                        <text x={L - 10} y={y + bh / 2 + 4} textAnchor="end" style={labelText}>{shortName(a.name)}</text>
                        <path d={hbar(L, y, Math.max(2, x(a.durationSeconds) - L), bh)} fill={MARK} />
                        <text x={x(a.durationSeconds) + 8} y={y + bh / 2 + 4} style={valueText}>{formatDuration(a.durationSeconds)}</text>
                    </g>
                );
            })}
            <line x1={x(avg)} x2={x(avg)} y1={T - 4} y2={H - 20} stroke={AXIS} strokeDasharray="3 3" />
            <text x={x(avg)} y={T - 10} textAnchor="middle" style={axisText}>{`prom. ${formatDuration(avg)}`}</text>
        </svg>
    );
};

export const TimelineChart = ({ attempts }: { attempts: QuizAttempt[] }) => {
    const rows = attempts.filter((a) => a.startedAt && a.finishedAt)
        .sort((a, b) => (a.startedAt as Date).getTime() - (b.startedAt as Date).getTime());
    if (!rows.length) return <p style={{ color: AXIS, margin: 0 }}>Sin horarios registrados.</p>;

    const W = 460, L = 130, R = 16, T = 14, bh = 20, gap = 20;
    const H = T + rows.length * (bh + gap) + 22;
    const min = Math.min(...rows.map((r) => (r.startedAt as Date).getTime()));
    const maxT = Math.max(...rows.map((r) => (r.finishedAt as Date).getTime()));
    const stepMs = niceStep(Math.max((maxT - min) / 1000, 600), 5) * 1000;
    const t0 = Math.floor(min / stepMs) * stepMs;
    const t1 = Math.ceil(maxT / stepMs) * stepMs;
    const x = (t: number) => L + ((t - t0) / (t1 - t0)) * (W - L - R);
    const multiDay = !dayjs(t0).isSame(dayjs(t1), "day");
    const ticks: number[] = [];
    for (let t = t0; t <= t1; t += stepMs) ticks.push(t);
    // Con rangos largos se muestran menos etiquetas para que no se encimen.
    const labelEvery = Math.ceil(ticks.length / 6);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Hora de inicio y fin de cada participante">
            {ticks.map((t, i) => (
                <g key={t}>
                    <line x1={x(t)} x2={x(t)} y1={T - 6} y2={H - 20} stroke={GRID} />
                    {i % labelEvery === 0 && (
                        <text x={x(t)} y={H - 4} textAnchor="middle" style={axisText}>
                            {dayjs(t).format(multiDay ? "DD/MM HH:mm" : "HH:mm")}
                        </text>
                    )}
                </g>
            ))}
            {rows.map((a, i) => {
                const y = T + 4 + i * (bh + gap);
                const start = (a.startedAt as Date).getTime();
                const end = (a.finishedAt as Date).getTime();
                return (
                    <g key={a.id}>
                        <title>{`${a.name} · ${dayjs(start).format("DD/MM HH:mm:ss")} → ${dayjs(end).format("HH:mm:ss")}`}</title>
                        <text x={L - 10} y={y + bh / 2 + 4} textAnchor="end" style={labelText}>{shortName(a.name)}</text>
                        <rect x={x(start)} y={y} width={Math.max(4, x(end) - x(start))} height={bh} rx={4} fill={MARK} />
                    </g>
                );
            })}
        </svg>
    );
};

export const QuestionAccuracyChart = ({ questions }: { questions: QuestionStat[] }) => {
    const W = 1040, L = 40, R = 8, T = 24, B = 28, H = 210;
    const ph = H - T - B;
    const slot = (W - L - R) / Math.max(questions.length, 1);
    const bw = Math.min(30, slot - 8);
    const y = (v: number) => T + ph - (v / 100) * ph;

    return (
        <div style={{ overflowX: "auto" }}>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 680 }} role="img" aria-label="Porcentaje de acierto por pregunta">
                {[0, 50, 100].map((v) => (
                    <g key={v}>
                        <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={v ? GRID : "#D9D9D9"} />
                        <text x={L - 8} y={y(v) + 4} textAnchor="end" style={axisText}>{`${v}%`}</text>
                    </g>
                ))}
                {questions.map((q, i) => {
                    const cx = L + slot * i + slot / 2;
                    const pct = Math.round((q.accuracy ?? 0) * 100);
                    return (
                        <g key={q.questionId}>
                            <title>{`P${q.order} · ${q.correct} de ${q.answered} (${pct}%) · respuesta ${q.key}\n${q.question}`}</title>
                            <rect x={cx - slot / 2} y={T - 18} width={slot} height={ph + 18 + B} fill="transparent" />
                            {pct > 0
                                ? <path d={vbar(cx - bw / 2, y(pct), bw, y(0) - y(pct))} fill={MARK} />
                                : <rect x={cx - bw / 2} y={y(0) - 2} width={bw} height={2} fill={MARK_SOFT} />}
                            <text x={cx} y={y(pct) - 6} textAnchor="middle" style={axisText}>{q.key}</text>
                            <text x={cx} y={H - 8} textAnchor="middle" style={axisText}>{q.order}</text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

export const KeyDistributionChart = ({ data, total }: { data: { letter: string; count: number }[]; total: number }) => {
    const W = 460, L = 34, R = 10, T = 20, B = 28, H = 200;
    const ph = H - T - B;
    const slot = (W - L - R) / data.length;
    const bw = 56;
    const maxV = Math.max(4, Math.ceil(Math.max(...data.map((d) => d.count)) / 4) * 4);
    const y = (v: number) => T + ph - (v / maxV) * ph;
    const ticks = [0, maxV / 4, maxV / 2, (maxV * 3) / 4, maxV];

    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Preguntas por letra de respuesta correcta">
            {ticks.map((v) => (
                <g key={v}>
                    <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={v ? GRID : "#D9D9D9"} />
                    <text x={L - 8} y={y(v) + 4} textAnchor="end" style={axisText}>{v}</text>
                </g>
            ))}
            {data.map(({ letter, count }, i) => {
                const cx = L + slot * i + slot / 2;
                return (
                    <g key={letter}>
                        <title>{`${count} preguntas · respuesta correcta ${letter} · ${total ? Math.round((count / total) * 100) : 0}% de la clave`}</title>
                        <rect x={cx - slot / 2 + 6} y={T - 10} width={slot - 12} height={ph + 10 + B} fill="transparent" />
                        {count
                            ? <path d={vbar(cx - bw / 2, y(count), bw, y(0) - y(count))} fill={MARK} />
                            : <rect x={cx - bw / 2} y={y(0) - 2} width={bw} height={2} fill={MARK_SOFT} />}
                        <text x={cx} y={y(count) - 8} textAnchor="middle" style={valueText}>{count}</text>
                        <text x={cx} y={H - 8} textAnchor="middle" style={{ fontSize: 12, fill: INK, fontWeight: 600 }}>{letter}</text>
                    </g>
                );
            })}
        </svg>
    );
};

export const LetterSpreadChart = ({ questions }: { questions: QuestionStat[] }) => {
    // Sin clave inferida: se muestra que letra eligio cada participante por pregunta.
    const letters = Array.from(new Set(questions.flatMap((q) => Object.keys(q.letters)))).sort();
    const shades = ["#FF0072", "#FF6FAE", "#FFB3D4", "#8C8C8C", "#D9D9D9"];
    const W = 1040, L = 40, R = 8, T = 16, B = 28, H = 200;
    const ph = H - T - B;
    const slot = (W - L - R) / Math.max(questions.length, 1);
    const bw = Math.min(30, slot - 8);

    return (
        <div style={{ overflowX: "auto" }}>
            <svg viewBox={`0 0 ${W} ${H + 20}`} width="100%" style={{ minWidth: 680 }} role="img" aria-label="Letras elegidas por pregunta">
                {questions.map((q, i) => {
                    const cx = L + slot * i + slot / 2;
                    let acc = 0;
                    return (
                        <g key={q.questionId}>
                            <title>{`P${q.order} · ${letters.map((l) => `${l}: ${q.letters[l] || 0}`).join(" · ")}\n${q.question}`}</title>
                            {letters.map((l, li) => {
                                const h = ((q.letters[l] || 0) / q.answered) * ph;
                                const yTop = T + ph - acc - h;
                                acc += h;
                                return h ? <rect key={l} x={cx - bw / 2} y={yTop} width={bw} height={h} fill={shades[li % shades.length]} stroke="#FFF" /> : null;
                            })}
                            <text x={cx} y={H - 8} textAnchor="middle" style={axisText}>{q.order}</text>
                        </g>
                    );
                })}
                {letters.map((l, li) => (
                    <g key={l} transform={`translate(${L + li * 60}, ${H + 8})`}>
                        <rect width={10} height={10} rx={2} fill={shades[li % shades.length]} />
                        <text x={14} y={9} style={axisText}>{l}</text>
                    </g>
                ))}
            </svg>
        </div>
    );
};
